import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { numberToLetter } from '@dealstack/shared/lib/format/number';
import { Text } from '@mantine/core';
import { memo, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import type { SubListItemV2Props } from './SubListItemV2.types';

export enum SubListItemV2State {
	default = 'default',
	completed = 'completed',
	incomplete = 'incomplete',
	active = 'active',
}

const SubListItemV2 = ({ className, listNumber, title, state, onClick }: SubListItemV2Props) => {
	const stateIconVariant: PredefinedIcons | undefined = useMemo(() => {
		switch (state) {
			case 'completed':
				return 'tick-circle';
			case 'incomplete':
				return `warning`;
		}
		return;
	}, [state]);

	return (
		<div className={className} onClick={onClick}>
			<Text
				c={state === SubListItemV2State.active ? 'dark.8' : 'dark.0'}
				fw={state === SubListItemV2State.active ? 600 : 400}
				lh="20px"
			>
				{numberToLetter(listNumber + 1)}&nbsp;
				{LONG_DASH}&nbsp;
				{title}
			</Text>
			{!!stateIconVariant && <Icon size="lg" variant={stateIconVariant} />}
		</div>
	);
};

const Memoized = memo(SubListItemV2);
const Styled = styled(Memoized)`
	display: flex;
	align-items: center;
	width: 100%;
	min-height: 1.25rem;
	user-select: none;

	${Icon} {
		color: ${({ theme }) => theme.v2.color.black[500]};
		margin-left: auto;
		padding-left: 0.5rem;
	}

	${({ onClick }) =>
		onClick &&
		css`
			cursor: pointer;
		`}

	${({ state, theme }) => {
		switch (state) {
			case SubListItemV2State.active:
				return css`
					&::before {
						content: '';
						display: block;
						border-radius: 4px;
						width: 4px;
						min-width: 4px;
						align-self: stretch;
						background-color: ${theme.v2.color.neutral[1100]};
						margin-right: 0.5rem;
					}
				`;
			case SubListItemV2State.incomplete:
				return css`
					&::before {
						content: '';
						display: block;
						min-width: 4px;
						border-radius: 4px;
						align-self: stretch;
						width: 4px;
						background-color: ${theme.v2.color.warning[800]};
						margin-right: 0.5rem;
					}
				`;

			default:
				return '';
		}
	}};
`;

export { Styled as SubListItemV2 };
